import { clamp, wrap } from "../math";
import type { Plan, PlannerConfig, PlannerTable } from "../types";
import type { CubeBelief } from "./belief";
import type { CubeRead } from "./sight";

const SLOTS = ["U", "D", "F", "B", "L", "R"];
const OTHER: Record<string, string> = { R: "B", L: "B", B: "R", F: "R" };

function emptyPlan(mode: Plan["mode"], label: string): Plan {
  return {
    mode,
    clip: -1,
    entry: 0,
    exit: -1,
    label,
    delta: "-",
    cost: Number.NaN,
    hold: 0,
    row: -1,
    sym: 0,
    yawOffset: 0,
    entryYaw: 0,
    matchedEntryYaw: 0,
    entryTranslation: 0,
    entryBearing: 0,
    entryForward: 0,
    entryLateral: 0,
    approachWindow: -1,
    approachRequested: 0,
    approachCovered: 0,
    approachTurn: false,
    searchScan: false,
    searchPhase: -1,
  };
}

export function stillPlan(cfg: PlannerConfig, yawOffset = 0, label?: string): Plan {
  const mode = yawOffset === 0 ? "settle" : "scan";
  return {
    ...emptyPlan(mode, label ?? mode),
    hold: mode === "scan" ? cfg.scan_steps : cfg.settle_steps,
    yawOffset,
  };
}

export class Clips {
  private rung = 0;
  private stall = 0;
  private readonly tried = new Set<number>();
  private lastColor = -1;
  private done = false;
  private committed = 0;

  constructor(
    private readonly table: PlannerTable,
    private readonly cfg: PlannerConfig,
    public targetColor: number,
  ) {}

  reset(): void {
    this.rung = 0;
    this.stall = 0;
    this.tried.clear();
    this.lastColor = -1;
    this.done = false;
    this.committed = 0;
  }

  setTarget(color: number): void {
    if (color === this.targetColor) return;
    this.targetColor = color;
    this.reset();
  }

  get solved(): boolean {
    return this.done;
  }

  get rungIndex(): number {
    return this.rung;
  }

  get ladderPattern(): string {
    return this.cfg.pattern;
  }

  get expectedTurn(): string {
    const pattern = this.ladderPattern;
    if (pattern.length === 0) return "—";
    return pattern[this.rung % pattern.length] ?? "—";
  }

  get committedClips(): number {
    return this.committed;
  }

  observe(belief: CubeBelief): void {
    if (!belief.valid) return;
    if (this.lastColor >= 0 && this.lastColor !== belief.color) {
      this.rung += 1;
      this.stall = 0;
      this.tried.clear();
    }
    this.lastColor = belief.color;
    if (belief.color === this.targetColor) this.done = true;
  }

  decide(belief: CubeBelief): Plan {
    if (this.done) return stillPlan(this.cfg);
    if (belief.valid && belief.poseOk && belief.pose !== null) {
      return this.retrieve(belief.pose);
    }
    if (belief.reads < (this.cfg.belief_scan_after_reads ?? this.cfg.belief_min_votes)) {
      return stillPlan(this.cfg);
    }
    if (!belief.valid || !belief.poseOk || belief.pose === null) {
      const full = (this.cfg.scan_sweep_deg * Math.PI) / 180;
      const sweep = belief.valid ? 0.25 * full : full;
      let yaw = clamp(belief.newest.hint, -sweep, sweep);
      if (Math.abs(yaw) < 0.25 * sweep) yaw = 0.25 * sweep * (yaw < 0 ? -1 : 1);
      return stillPlan(this.cfg, yaw);
    }
    return stillPlan(this.cfg);
  }

  still(yawOffset = 0, label?: string): Plan {
    return stillPlan(this.cfg, yawOffset, label);
  }

  commit(plan: Plan): void {
    if (plan.mode !== "clip") return;
    this.committed += 1;
    this.stall += 1;
    this.tried.add(plan.row);
  }

  retarget(anchor: Plan, sight: CubeRead): Plan {
    if (anchor.row < 0 || anchor.row >= this.table.clip.length) {
      throw new Error("cannot retarget an invalid clip row");
    }
    return this.candidate(sight, anchor.row, anchor.sym, anchor.delta);
  }

  retargetHeadingLocked(
    anchor: Plan,
    sight: CubeRead,
    robotYaw: number,
    targetWorldYaw: number,
  ): Plan {
    if (anchor.row < 0 || anchor.row >= this.table.clip.length) {
      throw new Error("cannot retarget an invalid clip row");
    }
    let bestSym = 0;
    let bestError = Infinity;
    for (let sym = 0; sym < 4; sym += 1) {
      const world = robotYaw + (this.table.qth[anchor.row] ?? 0)
        + sym * Math.PI / 2 + sight.phi;
      const error = Math.abs(wrap(world - targetWorldYaw));
      if (error < bestError) {
        bestError = error;
        bestSym = sym;
      }
    }
    return this.candidate(sight, anchor.row, bestSym, anchor.delta);
  }

  private retrieve(sight: CubeRead): Plan {
    const pattern = this.cfg.pattern;
    let delta = pattern[this.rung % pattern.length] ?? "R";
    if (this.cfg.retry_limit > 0 && this.stall >= this.cfg.retry_limit) delta = OTHER[delta] ?? delta;

    const candidates: number[] = [];
    for (let row = 0; row < this.table.clip.length; row += 1) {
      if (this.table.clean[row] && SLOTS[this.table.delta[row] ?? -1] === delta && !this.tried.has(row)) {
        candidates.push(row);
      }
    }
    if (candidates.length === 0) {
      for (let row = 0; row < this.table.clip.length; row += 1) {
        if (this.table.clean[row] && SLOTS[this.table.delta[row] ?? -1] === delta) candidates.push(row);
      }
    }
    if (candidates.length === 0) return stillPlan(this.cfg);

    const cs = Math.cos(-sight.phi);
    const sn = Math.sin(-sight.phi);
    const x = -sight.pos[0];
    const y = -sight.pos[1];
    const live = [cs * x - sn * y, sn * x + cs * y, -sight.phi];
    let bestRow = candidates[0] ?? 0;
    let bestSym = 0;
    let bestCost = Infinity;
    for (const row of candidates) {
      for (let sym = 0; sym < 4; sym += 1) {
        const a = sym * Math.PI / 2;
        const c = Math.cos(a);
        const s = Math.sin(a);
        const qx = this.table.qx[row] ?? 0;
        const qy = this.table.qy[row] ?? 0;
        const dx = c * qx - s * qy - (live[0] ?? 0);
        const dy = s * qx + c * qy - (live[1] ?? 0);
        const raw = (this.table.qth[row] ?? 0) + a - (live[2] ?? 0);
        const dth = Math.atan2(Math.sin(raw), Math.cos(raw));
        const cost = Math.hypot(dx, dy) + this.cfg.arm_radius * Math.abs(dth)
          + this.cfg.horizon_gain * Math.abs((this.table.exit_range[row] ?? 0) - this.cfg.stance_band_m);
        if (cost < bestCost) {
          bestCost = cost;
          bestRow = row;
          bestSym = sym;
        }
      }
    }
    return this.candidate(sight, bestRow, bestSym, delta);
  }

  private candidate(sight: CubeRead, row: number, sym: number, delta: string): Plan {
    const a = sym * Math.PI / 2;
    const c = Math.cos(a);
    const s = Math.sin(a);
    const qx = this.table.qx[row] ?? 0;
    const qy = this.table.qy[row] ?? 0;
    const rx = c * qx - s * qy;
    const ry = s * qx + c * qy;
    const cp = Math.cos(sight.phi);
    const sp = Math.sin(sight.phi);
    const entryForward = sight.pos[0] + cp * rx - sp * ry;
    const entryLateral = sight.pos[1] + sp * rx + cp * ry;
    const entryYaw = wrap((this.table.qth[row] ?? 0) + a + sight.phi);
    const horizon = this.cfg.horizon_gain
      * Math.abs((this.table.exit_range[row] ?? 0) - this.cfg.stance_band_m);
    const cost = Math.hypot(entryForward, entryLateral)
      + this.cfg.arm_radius * Math.abs(entryYaw) + horizon;
    const clip = this.table.clip[row] ?? 0;
    return {
      ...emptyPlan("clip", `${delta}#${clip}`),
      clip,
      entry: this.table.entry[row] ?? 0,
      exit: this.table.exit[row] ?? 0,
      delta,
      cost,
      row,
      sym,
      entryYaw,
      matchedEntryYaw: entryYaw,
      entryTranslation: Math.hypot(entryForward, entryLateral),
      entryBearing: Math.atan2(entryLateral, entryForward),
      entryForward,
      entryLateral,
    };
  }
}
